import classNames from "classnames";
import classes from "~/features/configurator/Shaft/substeps/ShaftMachiningSubstep/components/SegmentCommonStyles.module.less";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { Card } from "~/features/configurator/Linear/components/Card";
import { SimpleCsticId } from "~/features/configurator/Linear/models";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { radialThread } from "~/features/configurator/Shaft/metric/metricCsticConstants";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import { SimpleCsticNumberField } from "~/features/configurator/Shaft/components/SimpleCsticNumberField";
import { LinearTileV2 } from "~/features/configurator/Shaft/substeps/LinearTileV2";
import { SimpleCsticTileField } from "~/features/configurator/Shaft/components/SimpleCsticTileField";
import { AlarInput } from "~/features/configurator/Shaft/substeps/ShaftMachiningSubstep/components/InchShaftRadialThreadSegment/InchShaftRadialThreadSegment";
import { METRIC_UNIT } from "~/features/configurator/Shaft/common/commonShaftConstants";
import {
  ErrorMessage,
  selectIsMachiningExtraSubstepInvalid,
} from "~/features/configurator/Shaft/metric/components/MetricShaftMachiningExtraSubstep/MetricShaftMachiningExtraSubstepHelper";
import { useSelector } from "react-redux";
import { selectMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import { validateRadialThreadMetric, type ValidationMode } from "~/features/configurator/Shaft/shaftConfiguratorHelper";
import type { RootState } from "~/app/store";
import { selectConflictedCstics } from "~/features/configurator/Linear/redux/selectors";
import { selectCstic, selectCsticExpanded } from "~/features/configurator/Linear/redux/selectCstic";
import LinearInfo from "~/features/configurator/Linear/components/LinearInfo";

const AUTOMATIC_ALAR = "S";
const MANUAL_ALAR = "A";

const CONFIG = {
  image: "configurator.shaft.step.2.substep.3.extraFields.center.image",
  size: SimpleCsticId(radialThread.size),
  depth: {
    label: "configurator.shaft.step.3.details.radialThread.depth.title",
    csticId: SimpleCsticId(radialThread.threadDepth),
  },
  recommendedSpacing: {
    csticId: SimpleCsticId(radialThread.threadSpacing.recommended),
    csticsToSet: [
      {
        key: radialThread.threadSpacing.mode,
        value: radialThread.threadSpacing.modeRecommended,
      },
      {
        key: radialThread.threadSpacing.individual.value,
        value: null,
      },
    ],
  },
  individualSpacing: {
    label: "configurator.shaft.step.3.details.radialThread.boreSpacing.individual.title",
    value: SimpleCsticId(radialThread.threadSpacing.individual.value),
    min: SimpleCsticId(radialThread.threadSpacing.individual.min),
    max: SimpleCsticId(radialThread.threadSpacing.individual.max),
    unit: METRIC_UNIT,
    csticsToSet: [
      {
        key: radialThread.threadSpacing.mode,
        value: radialThread.threadSpacing.modeIndividual,
      },
      {
        key: radialThread.threadSpacing.recommended,
        value: null,
      },
    ],
  },
  al: {
    label: "configurator.shaft.step.2.substep.3.extraFields.al.title.metric",
    value: SimpleCsticId(radialThread.aL.value),
    min: SimpleCsticId(radialThread.aL.min),
    max: SimpleCsticId(radialThread.aL.max),
    unit: METRIC_UNIT,
    csticsToSet: [
      (val: string | null) => ({ key: radialThread.aLaRMode, value: val === null ? AUTOMATIC_ALAR : MANUAL_ALAR }),
      {
        key: radialThread.aR.value,
        value: null,
      },
    ],
  },
  ar: {
    label: "configurator.shaft.step.2.substep.3.extraFields.ar.title.metric",
    value: SimpleCsticId(radialThread.aR.value),
    min: SimpleCsticId(radialThread.aR.min),
    max: SimpleCsticId(radialThread.aR.max),
    unit: METRIC_UNIT,
    csticsToSet: [
      (val: string | null) => ({ key: radialThread.aLaRMode, value: val === null ? AUTOMATIC_ALAR : MANUAL_ALAR }),
      {
        key: radialThread.aL.value,
        value: null,
      },
    ],
  },
  threadHoleAmount: {
    label: "configurator.shaft.step.2.substep.3.extraFields.holeQuantity.title",
    csticId: SimpleCsticId(radialThread.threadHoleAmount),
  },
  info: "configurator.shaft.step.2.substep.3.tile.980.infoPopup.metric",
};

export const MetricShaftRadialThreadSegment = () => {
  const t = useXmlConfiguratorTranslations().t!;

  const errors = useSelector(selectErrors);
  const invalidSize = !!errors.find((error) => error.cstic === CONFIG.size.key);

  const threadSpacingMode = useSelector(selectCstic(SimpleCsticId(radialThread.threadSpacing.mode)))?.value;
  const isIndividualMode = threadSpacingMode !== radialThread.threadSpacing.modeRecommended;

  const invalidSpacingIndividual = !!errors.find((error) => error.cstic === CONFIG.individualSpacing.value.key);
  const invalidSpacingRecommended = !!errors.find((error) => error.cstic === CONFIG.recommendedSpacing.csticId.key);
  const invalidSpacing = invalidSpacingIndividual || invalidSpacingRecommended;

  const alarInvalid = !!errors.find(
    (error) => error.cstic === CONFIG.al.value.key || error.cstic === CONFIG.ar.value.key,
  );

  return (
    <div className={classNames(classes.root)}>
      <div className={classes.titleSection}>
        <LanguageAwareExternalContent
          content={"configurator.shaft.step.3.details.radialThread.title"}
          className={classNames("scha__lc__external-label", classes.segmentTitle)}
        />
        <LinearInfo info={CONFIG.info} />
      </div>
      <Card className={classes.imageSection}>
        <img className={classes.image} src={t(CONFIG.image)} alt="" />
      </Card>
      <div className={classes.inputSection}>
        {invalidSize && <ErrorMessage />}
        <SimpleCsticTileField
          className={classes.fullWidth}
          csticId={CONFIG.size}
          substepType={SubstepType.ShaftMachiningExtra}
          tilesPerRow={4}
          renderTile={({ onClick, disabled, selected, value }) => (
            <LinearTileV2.Base onClick={onClick} selected={selected} disabled={disabled}>
              <LinearTileV2.CheckedIndicator />
              <p className="m-0">{value.value}</p>
            </LinearTileV2.Base>
          )}
        />
        <SimpleCsticNumberField
          {...CONFIG.depth}
          substepType={SubstepType.ShaftMachiningExtra}
          decimalScale={2}
          disabled={true}
        />
        <p className={classes.fieldTitle}>
          {t("configurator.shaft.step.3.details.radialThread.boreSpacing.recommended.title")}
        </p>
        {invalidSpacing && <ErrorMessage variant={isIndividualMode ? "input" : "select"} />}
        <SimpleCsticTileField
          className={classes.fullWidth}
          {...CONFIG.recommendedSpacing}
          substepType={SubstepType.ShaftMachiningExtra}
          required={!isIndividualMode}
          tilesPerRow={4}
          renderTile={({ onClick, disabled, selected, value }) => (
            <LinearTileV2.Base onClick={onClick} selected={selected} disabled={disabled}>
              <LinearTileV2.CheckedIndicator />
              <p className="m-0">{value.label}</p>
            </LinearTileV2.Base>
          )}
        />
        <AlarInput
          {...CONFIG.individualSpacing}
          substepType={SubstepType.ShaftMachiningExtra}
          decimalScale={2}
          required={isIndividualMode}
        />
        <div className={classes.alarSection}>
          <p className={classes.alarDescription}>{t("configurator.shaft.step.2.substep.3.extraFields.alar.title")}</p>
          {!invalidSpacing && alarInvalid && <ErrorMessage variant={"input"} />}
          <div className={classes.alarRow}>
            <AlarInput {...CONFIG.al} substepType={SubstepType.ShaftMachiningExtra} required />
            <AlarInput {...CONFIG.ar} substepType={SubstepType.ShaftMachiningExtra} disabled={true} />
          </div>
        </div>
        <SimpleCsticNumberField
          {...CONFIG.threadHoleAmount}
          substepType={SubstepType.ShaftMachiningExtra}
          decimalScale={2}
          disabled={true}
        />
      </div>
    </div>
  );
};

const selectErrors = (state: RootState) => {
  const isSubstepInvalid = selectIsMachiningExtraSubstepInvalid(state);
  const mode: ValidationMode = "full";

  const kmat = selectMainKmat(state);
  const conflictedCstics = selectConflictedCstics(state);
  let errors = validateRadialThreadMetric({ kmat, conflictedCstics, mode });

  if (!isSubstepInvalid) {
    // We need to remove pristine fields if substep is not invalid
    [
      CONFIG.size,
      CONFIG.individualSpacing.value,
      CONFIG.recommendedSpacing.csticId,
      CONFIG.al.value,
      CONFIG.ar.value,
    ].forEach((cstic) => {
      const isDirty = selectCsticExpanded(cstic, SubstepType.ShaftMachiningExtra)(state).isDirty;
      if (!isDirty) {
        errors = errors.filter((error) => error.cstic !== cstic.key || error.type !== "required");
      }
    });
  }

  return errors;
};
